import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QuickTechnicalReportModal.css';

// Función para obtener fecha/hora actual en formato datetime-local (zona horaria local)
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Función para formatear fecha en AM/PM
const formatDateTimeAMPM = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('es-DO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const QuickTechnicalReportModal = ({ isOpen, onClose, contact, onReportCreated }) => {
  const [formData, setFormData] = useState({
    tipoFalla: '',
    fecha: new Date().toISOString().slice(0, 16),
    tiempo: '',
    descripcion: '',
    tecnico: ''
  });
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadTechnicians();
      // Resetear formulario con fecha/hora actual en formato local
      setFormData({
        tipoFalla: '',
        fecha: getCurrentDateTimeLocal(),
        tiempo: '',
        descripcion: '',
        tecnico: ''
      });
    }
  }, [isOpen]);

  const loadTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/technicians', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTechnicians(response.data.data.filter(t => t.isActive));
      }
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipoFalla) {
      alert('Por favor selecciona el tipo de falla');
      return;
    }

    if (!formData.tecnico) {
      alert('Por favor selecciona un técnico');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Determinar prioridad según tipo de falla
      let priority = 'media';
      if (formData.tipoFalla === 'Sin potencia' || formData.tipoFalla === 'Equipo no enciende') {
        priority = 'alta';
      }

      // Determinar tipo de reporte
      let type = 'averia';
      if (formData.tipoFalla === 'Cambio de equipo') {
        type = 'mantenimiento';
      } else if (formData.tipoFalla === 'Solicitud de instalación') {
        type = 'instalacion';
      }

      // Convertir tiempo seleccionado a minutos
      let tiempoLimite = null;
      if (formData.tiempo && formData.tiempo !== 'Otro') {
        const tiempoStr = formData.tiempo.toLowerCase();
        // Verificar primero las opciones más largas para evitar coincidencias parciales
        if (tiempoStr.includes('2 horas 30 minutos') || tiempoStr.includes('2.5 horas')) {
          tiempoLimite = 150;
        } else if (tiempoStr.includes('1 hora 30 minutos') || tiempoStr.includes('1.5 horas')) {
          tiempoLimite = 90;
        } else if (tiempoStr.includes('24 horas')) {
          tiempoLimite = 1440;
        } else if (tiempoStr.includes('12 horas')) {
          tiempoLimite = 720;
        } else if (tiempoStr.includes('8 horas')) {
          tiempoLimite = 480;
        } else if (tiempoStr.includes('6 horas')) {
          tiempoLimite = 360;
        } else if (tiempoStr.includes('5 horas')) {
          tiempoLimite = 300;
        } else if (tiempoStr.includes('4 horas')) {
          tiempoLimite = 240;
        } else if (tiempoStr.includes('3 horas')) {
          tiempoLimite = 180;
        } else if (tiempoStr.includes('2 horas')) {
          tiempoLimite = 120;
        } else if (tiempoStr.includes('1 hora')) {
          tiempoLimite = 60;
        } else if (tiempoStr.includes('45 minutos')) {
          tiempoLimite = 45;
        } else if (tiempoStr.includes('30 minutos')) {
          tiempoLimite = 30;
        } else if (tiempoStr.includes('15 minutos')) {
          tiempoLimite = 15;
        }
      }

      // Si no se seleccionó tiempo, usar el tiempo límite por defecto según prioridad
      if (!tiempoLimite) {
        if (priority === 'urgente') {
          tiempoLimite = 30;
        } else if (priority === 'alta') {
          tiempoLimite = 60;
        } else if (priority === 'media') {
          tiempoLimite = 120;
        } else if (priority === 'baja') {
          tiempoLimite = 180;
        } else {
          tiempoLimite = 60; // Por defecto
        }
      }

      // Crear título y descripción
      const title = `Reporte: ${formData.tipoFalla}`;
      const description = formData.descripcion || `Falla reportada: ${formData.tipoFalla}${formData.tiempo ? ` - Tiempo: ${formData.tiempo}` : ''}`;

      const selectedTech = technicians.find(t => t.name === formData.tecnico);
      
      const reportData = {
        type,
        priority,
        phoneNumber: contact.phoneNumber,
        clientName: contact.name || contact.phoneNumber,
        mikrowispId: contact.mikrowispId || '',
        clientAddress: contact.address || '',
        title,
        description,
        technician: formData.tecnico,
        technicianPhone: selectedTech ? selectedTech.phone : '',
        scheduledDate: formData.fecha ? new Date(formData.fecha).toISOString() : null,
        notes: formData.tiempo ? `Tiempo reportado: ${formData.tiempo}` : '',
        tiempoLimite // Enviar el tiempo límite en minutos
      };

      const response = await axios.post('/api/technical-reports', reportData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('✅ Reporte técnico creado exitosamente');
        if (onReportCreated) {
          onReportCreated(response.data.data);
        }
        onClose();
      } else {
        alert('Error al crear reporte: ' + (response.data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error creando reporte:', error);
      alert('Error al crear reporte: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quick-report-modal-overlay" onClick={onClose}>
      <div className="quick-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quick-report-modal-header">
          <h2>🔴 Reporte Técnico Rápido</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="quick-report-form">
          <div className="form-group">
            <label>Tipo de Falla *</label>
            <select
              name="tipoFalla"
              value={formData.tipoFalla}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar tipo de falla...</option>
              <option value="Solicitud de instalación">📦 Solicitud de instalación</option>
              <option value="Sin potencia">Sin potencia</option>
              <option value="Equipo no enciende">Equipo no enciende</option>
              <option value="WIFI no funciona">WIFI no funciona</option>
              <option value="Internet Lento">Internet Lento</option>
              <option value="Cambio de equipo">Cambio de equipo</option>
              <option value="Otros">Otros</option>
            </select>
          </div>

          <div className="form-group">
            <label>Fecha y Hora *</label>
            <div className="datetime-selector">
              <input
                type="datetime-local"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                required
                className="datetime-input"
              />
              <div className="quick-time-buttons">
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      fecha: getCurrentDateTimeLocal()
                    }));
                  }}
                  className="quick-time-btn"
                  title="Ahora"
                >
                  Ahora
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const in30min = new Date(Date.now() + 30 * 60000);
                    const year = in30min.getFullYear();
                    const month = String(in30min.getMonth() + 1).padStart(2, '0');
                    const day = String(in30min.getDate()).padStart(2, '0');
                    const hours = String(in30min.getHours()).padStart(2, '0');
                    const minutes = String(in30min.getMinutes()).padStart(2, '0');
                    setFormData(prev => ({
                      ...prev,
                      fecha: `${year}-${month}-${day}T${hours}:${minutes}`
                    }));
                  }}
                  className="quick-time-btn"
                  title="En 30 minutos"
                >
                  +30 min
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const in1hour = new Date(Date.now() + 60 * 60000);
                    const year = in1hour.getFullYear();
                    const month = String(in1hour.getMonth() + 1).padStart(2, '0');
                    const day = String(in1hour.getDate()).padStart(2, '0');
                    const hours = String(in1hour.getHours()).padStart(2, '0');
                    const minutes = String(in1hour.getMinutes()).padStart(2, '0');
                    setFormData(prev => ({
                      ...prev,
                      fecha: `${year}-${month}-${day}T${hours}:${minutes}`
                    }));
                  }}
                  className="quick-time-btn"
                  title="En 1 hora"
                >
                  +1 hora
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const in2hours = new Date(Date.now() + 120 * 60000);
                    const year = in2hours.getFullYear();
                    const month = String(in2hours.getMonth() + 1).padStart(2, '0');
                    const day = String(in2hours.getDate()).padStart(2, '0');
                    const hours = String(in2hours.getHours()).padStart(2, '0');
                    const minutes = String(in2hours.getMinutes()).padStart(2, '0');
                    setFormData(prev => ({
                      ...prev,
                      fecha: `${year}-${month}-${day}T${hours}:${minutes}`
                    }));
                  }}
                  className="quick-time-btn"
                  title="En 2 horas"
                >
                  +2 horas
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(9, 0, 0, 0);
                    const year = tomorrow.getFullYear();
                    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
                    const day = String(tomorrow.getDate()).padStart(2, '0');
                    setFormData(prev => ({
                      ...prev,
                      fecha: `${year}-${month}-${day}T09:00`
                    }));
                  }}
                  className="quick-time-btn"
                  title="Mañana a las 9:00 AM"
                >
                  Mañana 9AM
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Tiempo</label>
            <select
              name="tiempo"
              value={formData.tiempo}
              onChange={handleChange}
            >
              <option value="">Seleccionar tiempo...</option>
              <option value="15 minutos">15 minutos</option>
              <option value="30 minutos">30 minutos</option>
              <option value="45 minutos">45 minutos</option>
              <option value="1 hora">1 hora</option>
              <option value="1 hora 30 minutos">1 hora 30 minutos</option>
              <option value="2 horas">2 horas</option>
              <option value="2 horas 30 minutos">2 horas 30 minutos</option>
              <option value="3 horas">3 horas</option>
              <option value="4 horas">4 horas</option>
              <option value="5 horas">5 horas</option>
              <option value="6 horas">6 horas</option>
              <option value="8 horas">8 horas</option>
              <option value="12 horas">12 horas</option>
              <option value="24 horas">24 horas</option>
              <option value="Otro">Otro (especificar en descripción)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Técnico Asignado *</label>
            <select
              name="tecnico"
              value={formData.tecnico}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar técnico...</option>
              {technicians.map(tech => (
                <option key={tech._id} value={tech.name}>
                  {tech.name} - {tech.phone}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Descripción Adicional</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows="3"
              placeholder="Descripción adicional del problema..."
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancelar
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar Reporte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickTechnicalReportModal;

